// Portal reading logic (Phase 3) — derives the article corpus, cards, search and
// onboarding from the fixture in corpus.ts. Videos live in the SAME corpus as
// written articles (DECISIONS.md / V3_README §A — "videos are articles"), so
// search, categories and provenance treat them identically. Entitlement default
// is `mark` (DECISIONS.md §4): add-on content stays visible with an honest
// banner, never hidden.

import { BRANDS, type Brand, type BrandId } from "./brands";
import { RAW, type RawArticle } from "./corpus";

export type EntitlementMode = "filter" | "mark" | "show";

export interface Article extends RawArticle {
  kind: "article" | "video";
  len?: string; // "12:34" for videos
}

export interface ArticleCard {
  id: string;
  title: string;
  cat: string;
  excerpt: string;
  audience: string;
  updated: string;
  views: string;
  gated: boolean; // add-on the reader's org doesn't license (shown, marked)
  isVideo: boolean;
  len: string;
  kindLabel: string; // "8 min read" | "Watch · 22:40"
  source: { label: string; tenant: boolean }; // provenance (platform brand vs tenant/org)
}

const isTenant = (id: string) => RAW.tenantAuthored.includes(id);

/**
 * A tenant-authored article belongs to exactly ONE client space — the brand's
 * home client space — so it shows in that space and NOT in the brand's other
 * client spaces (Coastal's docs must not appear in Triangle's space). The fixture
 * doesn't map each tenant doc to a client, so they're all attributed to the home
 * space; the real read rule keys on `article.client_id` (DATA_MODEL.md §1).
 */
function homeSpace(brandId: BrandId): string | undefined {
  return BRANDS[brandId].spaces.find((s) => s.kind === "Client space")?.id;
}

/** Parse a views label ("18.7k", "980", "1.2m") to a number for ranking. */
export function viewsNum(v: string): number {
  const m = /^([\d.]+)\s*([km]?)/i.exec(v.trim());
  if (!m) return 0;
  const n = parseFloat(m[1]!);
  const mult = m[2]?.toLowerCase() === "m" ? 1e6 : m[2]?.toLowerCase() === "k" ? 1e3 : 1;
  return Number.isFinite(n) ? n * mult : 0;
}

/**
 * The corpus for a brand + space (§1 read rule). Platform docs show only
 * platform-origin articles; a client space additionally shows that org's
 * tenant-authored articles. Written articles + videos-as-articles.
 */
export function articlesFor(brandId: BrandId, spaceId: string = "platform", mode: EntitlementMode = "mark"): Article[] {
  // Platform space: platform docs only. A client space: platform docs + only the
  // tenant docs that belong to THAT space (its home space), never another
  // client's docs.
  const home = homeSpace(brandId);
  const inSpace = (id: string) => {
    if (!isTenant(id)) return true; // platform docs surface everywhere for the brand
    return spaceId !== "platform" && spaceId === home;
  };
  const written: Article[] = RAW.articles
    .filter((a) => a.brand === brandId && inSpace(a.id))
    .map((a) => ({ ...a, kind: "article" as const }));

  const videos: Article[] = RAW.videos
    .filter((v) => v[3] === brandId)
    .map((v, i) => {
      const [title, len, audience, brand, cat, views] = v;
      const meta = RAW.videoMeta[title] ?? ["", []];
      const mins = Math.max(1, Math.round(parseInt(len.split(":")[0]!, 10) + parseInt(len.split(":")[1]!, 10) / 60));
      const body: unknown[] = [["vid", title, len, `vid-${brand}-${i}`]];
      if (meta[1].length) body.push(["h", "Chapters"], ["chapters", meta[1]]);
      body.push(["h", "Prefer to read it?"], ["p", "Every video is cut from an article in this knowledge base, and the article is kept in step with it. Use the related list to open the written version."]);
      return { id: `vid-${brand}-${i}`, brand, cat, title, audience, plan: "Included", mins, updated: "Sep 2, 2026", views, excerpt: meta[0], kind: "video" as const, len, body };
    });

  const all = [...written, ...videos];
  return mode === "filter" ? all.filter((a) => a.plan !== "Add-on") : all;
}

/** Card view-model for an article, carrying provenance and entitlement. */
export function toCard(a: Article, brand: Brand, mode: EntitlementMode, orgName: string): ArticleCard {
  const gated = a.plan === "Add-on" && mode !== "show";
  const tenant = isTenant(a.id);
  return {
    id: a.id,
    title: a.title,
    cat: a.cat,
    excerpt: a.excerpt,
    audience: a.audience,
    updated: a.updated,
    views: a.views,
    gated,
    isVideo: a.kind === "video",
    len: a.len ?? "",
    kindLabel: a.kind === "video" ? `Watch · ${a.len}` : `${a.mins} min read`,
    source: { label: tenant ? orgName : brand.name, tenant },
  };
}

/** Resolve a single article for a deep link. Client-space scope also sees tenant docs. */
export function findArticle(brandId: BrandId, id: string, spaceId: string = "platform"): Article | undefined {
  return articlesFor(brandId, spaceId, "show").find((a) => a.id === id);
}

export interface CategoryCard {
  icon: string;
  name: string;
  blurb: string;
  audience: string;
  count: string; // "12 articles" — computed from the corpus, not the static number
}

/** Categories for a brand + space, filtered by audience, with LIVE article counts. */
export function categoriesFor(brandId: BrandId, spaceId: string, audience: string, mode: EntitlementMode = "mark"): CategoryCard[] {
  const arts = articlesFor(brandId, spaceId, mode);
  return BRANDS[brandId].cats
    .filter(([, , , , who]) => audience === "All" || who === audience)
    .map(([icon, name, blurb, , who]) => {
      const n = arts.filter((a) => a.cat === name).length;
      return { icon, name, blurb, audience: who, count: n === 1 ? "1 article" : `${n} articles` };
    });
}

/** Live per-category counts for a brand+space, keyed by category name (for the guides rail). */
export function categoryCounts(brandId: BrandId, spaceId: string, mode: EntitlementMode = "mark"): Record<string, number> {
  const arts = articlesFor(brandId, spaceId, mode);
  const out: Record<string, number> = {};
  for (const [, name] of BRANDS[brandId].cats) out[name] = arts.filter((a) => a.cat === name).length;
  return out;
}

export interface SearchResult {
  results: ArticleCard[];
  answer: { text: string; confidence: number; sources: [string, string][] } | null;
}

/**
 * Retrieval over the corpus (DECISIONS.md §7 — the answer is bounded by its
 * sources and cites them). A canned answer is used when the query matches a
 * known intent; otherwise a best-match answer points at the top result and never
 * guesses. Keyword rank; the real embedding retrieval lands with the read API.
 */
export function search(brandId: BrandId, spaceId: string, brand: Brand, mode: EntitlementMode, orgName: string, q: string): SearchResult {
  const arts = articlesFor(brandId, spaceId, mode);
  const qq = q.trim().toLowerCase();
  const tokens = qq.split(/[^a-z0-9]+/).filter((w) => w.length > 2);
  const scored = arts
    .map((a) => {
      const hay = `${a.title} ${a.excerpt} ${a.cat} ${a.audience}`.toLowerCase();
      return { a, n: tokens.reduce((t, w) => t + (hay.includes(w) ? 1 : 0), 0) };
    })
    .filter((x) => x.n > 0)
    .sort((x, y) => y.n - x.n);
  const ranked = scored.map((x) => x.a).slice(0, 6);
  const results = ranked.map((a) => toCard(a, brand, mode, orgName));

  let answer: SearchResult["answer"] = null;
  // No answer at all for an empty query or a query with no real search terms
  // (all stop-words) — never fabricate one (DECISIONS.md §7).
  if (tokens.length > 0) {
    const canned = RAW.askAnswers[brandId];
    const cannedHits = canned ? canned.match.filter((m) => qq.includes(m)).length : 0;
    const topScore = scored[0]?.n ?? 0;
    if (canned && cannedHits >= 2) {
      // A canned high-confidence answer must be a real intent match (≥2 keywords),
      // not a single stray substring.
      answer = { text: canned.answer, confidence: canned.confidence ?? 82, sources: canned.sources ?? (ranked[0] ? [[ranked[0].id, ranked[0].title]] : []) };
    } else if (ranked[0] && topScore >= 2) {
      // A best-match answer only when the top result genuinely matches multiple
      // terms — otherwise we'd present an unrelated article as "the answer".
      const top = ranked[0];
      answer = { text: `The closest match is “${top.title}”. ${top.excerpt} If that isn't what you meant, rephrase in your own words or open a ticket and a person will answer.`, confidence: 62, sources: [[top.id, top.title]] };
    } else {
      // Honest low-confidence: no confident answer; point at results / a ticket.
      answer = { text: "I couldn't find a confident answer in this space. The results below may help, or open a ticket and a person will answer.", confidence: 0, sources: [] };
    }
  }
  return { results, answer };
}

export function pathsFor(brandId: BrandId) {
  return (RAW.paths[brandId] ?? []).map((p) => ({
    ...p,
    stepNames: RAW.pathSteps[p.name] ?? [],
  }));
}
